import React, { useEffect } from 'react';
import { usePageTracking } from '../hooks/usePageTracking';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertTriangle, FileText, Scale, Clock, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { firmInfo } from '../data/mock';

const platformData = {
  'facebook-account-dispute': {
    name: 'Facebook',
    displayName: 'Facebook Account Dispute',
    metaTitle: 'Facebook Account Disabled? Appeal Documentation Help | Social Dispute Co.',
    metaDescription: 'Facebook account disabled? We help prepare appeal documentation. Independent service - not affiliated with Meta. No guarantees.',
    heroTitle: 'Facebook Account Disabled?',
    heroSubtitle: 'We help organize documentation for submission through official Meta appeal channels.',
    color: '#1877F2',
    commonReasons: [
      'Community Standards violations',
      'Suspicious activity detection',
      'Identity verification failures',
      'Ad policy violations',
      'Automated enforcement errors',
      'Association with disabled accounts',
      'Hacked account activity'
    ],
    specificInfo: 'Meta uses automated enforcement systems that can cascade across connected accounts, pages, and ad accounts. Our documentation addresses these specific enforcement patterns and escalates through proper legal channels.',
    registeredAgent: 'Meta Platforms, Inc. requires formal disputes to be submitted through their registered agent for legal correspondence.'
  },
  'instagram-account-dispute': {
    name: 'Instagram',
    displayName: 'Instagram Account Dispute',
    metaTitle: 'Instagram Account Suspended? Appeal Documentation Help | Social Dispute Co.',
    metaDescription: 'Instagram account disabled or suspended? We help prepare appeal documentation. Independent service - not affiliated with Meta. No guarantees.',
    heroTitle: 'Instagram Account Suspended?',
    heroSubtitle: 'We help organize documentation for submission through official Meta appeal channels.',
    color: '#E4405F',
    commonReasons: [
      'Terms of Service violations',
      'Intellectual property claims',
      'Impersonation reports',
      'Automated spam detection',
      'Linked account enforcement',
      'Age verification issues',
      'Hacked account activity'
    ],
    specificInfo: 'Instagram accounts are governed by Meta\'s unified enforcement system. Suspensions often cascade from Facebook enforcement or linked business assets. We address cross-platform enforcement in our documentation.',
    registeredAgent: 'Instagram (Meta Platforms, Inc.) legal correspondence goes through their designated registered agent.'
  },
  'tiktok-account-dispute': {
    name: 'TikTok',
    displayName: 'TikTok Account Dispute',
    metaTitle: 'TikTok Account Banned? Appeal Help & Dispute Services | Social Dispute Co.',
    metaDescription: 'TikTok account banned or suspended? We prepare formal dispute packages for TikTok\'s legal and trust & safety teams.',
    heroTitle: 'TikTok Account Banned?',
    heroSubtitle: 'Professional dispute documentation prepared for TikTok\'s legal and trust & safety review teams.',
    color: '#000000',
    commonReasons: [
      'Community Guidelines violations',
      'Minor safety concerns',
      'Spam or fake engagement',
      'Intellectual property claims',
      'Multiple account violations',
      'Device or network bans',
      'Appeals denied without explanation'
    ],
    specificInfo: 'TikTok employs aggressive automated enforcement that can result in permanent bans with limited appeal options. Our documentation addresses TikTok\'s specific legal framework and escalation pathways.',
    registeredAgent: 'TikTok (ByteDance Ltd.) has specific legal channels for formal dispute correspondence.'
  },
  'youtube-account-dispute': {
    name: 'YouTube',
    displayName: 'YouTube Channel Dispute',
    metaTitle: 'YouTube Channel Terminated? Appeal Documentation Help | Social Dispute Co.',
    metaDescription: 'YouTube channel terminated or suspended? We help prepare appeal documentation. Independent service - not affiliated with Google. No guarantees.',
    heroTitle: 'YouTube Channel Terminated?',
    heroSubtitle: 'We help organize documentation for submission through official YouTube appeal channels.',
    color: '#FF0000',
    commonReasons: [
      'Community Guidelines strikes',
      'Copyright strike accumulation',
      'Terms of Service violations',
      'Spam and deceptive practices',
      'Monetization policy violations',
      'Linked account terminations',
      'Coordinated behavior flags'
    ],
    specificInfo: 'YouTube channel terminations often involve accumulated strikes or enforcement across linked Google accounts. Our documentation addresses the specific violation cited and presents relevant evidence for your appeal.',
    registeredAgent: 'YouTube (Google LLC) accepts formal legal correspondence through their designated registered agent.'
  },
  'x-twitter-account-dispute': {
    name: 'X (Twitter)',
    displayName: 'X / Twitter Account Dispute',
    metaTitle: 'X (Twitter) Account Suspended? Appeal Help & Dispute | Social Dispute Co.',
    metaDescription: 'X (Twitter) account suspended or banned? We prepare formal dispute documentation for X\'s legal and trust & safety teams.',
    heroTitle: 'X (Twitter) Account Suspended?',
    heroSubtitle: 'Professional dispute documentation prepared for X\'s legal review and trust & safety teams.',
    color: '#000000',
    commonReasons: [
      'Rules violations',
      'Spam or platform manipulation',
      'Abusive behavior reports',
      'Automation violations',
      'Coordinated behavior flags',
      'Verification issues',
      'Account compromise'
    ],
    specificInfo: 'X (formerly Twitter) has undergone significant policy changes. Our documentation addresses current enforcement practices and escalates through appropriate legal channels.',
    registeredAgent: 'X Corp. has specific legal correspondence requirements for formal disputes.'
  },
  'reddit-account-dispute': {
    name: 'Reddit',
    displayName: 'Reddit Account Dispute',
    metaTitle: 'Reddit Account Suspended? Appeal Help & Dispute | Social Dispute Co.',
    metaDescription: 'Reddit account suspended or banned? We prepare formal dispute documentation for Reddit\'s legal and trust & safety teams.',
    heroTitle: 'Reddit Account Suspended?',
    heroSubtitle: 'Formal dispute preparation for Reddit suspensions and subreddit bans.',
    color: '#FF4500',
    commonReasons: [
      'Content Policy violations',
      'Vote manipulation',
      'Ban evasion detection',
      'Harassment reports',
      'Spam behavior',
      'Moderator abuse claims',
      'IP-based enforcement'
    ],
    specificInfo: 'Reddit enforces account-level and IP-level bans. Our documentation addresses the specific violation and presents evidence for appeal through proper channels.',
    registeredAgent: 'Reddit, Inc. accepts formal legal correspondence through their designated registered agent.'
  },
  'snapchat-account-dispute': {
    name: 'Snapchat',
    displayName: 'Snapchat Account Dispute',
    metaTitle: 'Snapchat Account Locked? Appeal Documentation Help | Social Dispute Co.',
    metaDescription: 'Snapchat account locked or banned? We help prepare appeal documentation. Independent service - not affiliated with Snap Inc. No guarantees.',
    heroTitle: 'Snapchat Account Locked?',
    heroSubtitle: 'Documentation support for Snap Inc. appeal submissions.',
    color: '#FFFC00',
    commonReasons: [
      'Community Guidelines violations',
      'Spam or fake engagement',
      'Third-party app usage',
      'Device ban enforcement',
      'Age verification issues',
      'Account compromise',
      'Terms of Service violations'
    ],
    specificInfo: 'Snapchat employs device-level enforcement that can persist. Our documentation addresses both account and device-level issues.',
    registeredAgent: 'Snap Inc. has specific legal channels for formal dispute correspondence.'
  }
};

const PlatformDisputePage = () => {
  const location = useLocation();
  const platform = location.pathname.replace('/', '');
  const data = platformData[platform];
  usePageTracking(data ? `Platform: ${data.name}` : "Platform Page", { category: "platform" });

  // Map dispute pages to their canonical service appeal page (de-cannibalization).
  // For platforms that lack a parallel /services/ page (snapchat, reddit), the
  // page remains self-canonical.
  const DISPUTE_TO_SERVICE = {
    'facebook-account-dispute': '/services/facebook-account-appeal',
    'instagram-account-dispute': '/services/instagram-account-appeal',
    'tiktok-account-dispute': '/services/tiktok-account-appeal',
    'youtube-account-dispute': '/services/youtube-account-appeal',
    'x-twitter-account-dispute': '/services/x-account-appeal',
  };

  useEffect(() => {
    if (data) {
      document.title = data.metaTitle;
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', data.metaDescription);
      // Update canonical — point to /services/{platform}-account-appeal if a
      // parallel service page exists; otherwise self-canonical.
      const canonicalPath = DISPUTE_TO_SERVICE[platform] || location.pathname;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute('href', `https://socialdisputeco.com${canonicalPath}`);
    }
    return () => {
      document.title = 'Instagram Suspended? Facebook, TikTok, YouTube Appeal Help | Social Dispute Co';
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute('href', 'https://socialdisputeco.com/');
    };
  }, [data, location.pathname, platform]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-[#d9fb06] mb-4">Platform Not Found</h1>
          <Link to="/" className="text-[#888680] hover:text-[#d9fb06]">Return Home</Link>
        </div>
      </div>
    );
  }

  const scrollToIntake = () => {
    window.location.href = '/#intake';
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="max-w-4xl">
            <div 
              className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: data.color + '20', color: data.color }}
            >
              {data.name} Dispute Services
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              {data.heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-[#888680] mb-8 leading-relaxed">
              {data.heroSubtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={scrollToIntake}
                className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 font-semibold text-base"
                data-testid="platform-cta-review"
              >
                Request a Review
              </Button>
              <a 
                href={`tel:${firmInfo.phone}`}
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#3f4816] rounded-full text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors"
              >
                <Phone className="w-4 h-4" />
                {firmInfo.phone}
              </a>
            </div>
          </div>
        </section>

        {/* Common Reasons Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-6 h-6 text-[#d9fb06]" />
                <h2 className="text-xl font-bold text-[#d9fb06]">Common Reasons for {data.name} Suspension</h2>
              </div>
              <ul className="space-y-3">
                {data.commonReasons.map((reason) => (
                  <li key={`${data.name}-reason-${reason}`} className="flex items-start gap-3 text-[#888680]">
                    <span className="text-[#d9fb06] mt-1">•</span>
                    {reason}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-[#d9fb06]" />
                <h2 className="text-xl font-bold text-[#d9fb06]">Platform-Specific Information</h2>
              </div>
              <p className="text-[#888680] leading-relaxed mb-6">
                {data.specificInfo}
              </p>
              <p className="text-[#888680] text-sm italic">
                {data.registeredAgent}
              </p>
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#d9fb06] mb-8">
              How We Help With {data.name} Disputes
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: FileText, title: 'Documentation Preparation', desc: 'Organized case documentation for appeal submission' },
                { icon: Scale, title: 'Policy Analysis', desc: 'Review of enforcement action against platform policies' },
                { icon: CheckCircle, title: 'Evidence Compilation', desc: 'Gathering supporting documentation for your case' },
                { icon: Clock, title: 'Timeline Organization', desc: 'Structured account history and key events' },
                { icon: Mail, title: 'Submission Guidance', desc: 'Prepared for official platform appeal channels' },
                { icon: Phone, title: 'Case Support', desc: 'Communication throughout the documentation process' }
              ].map((item) => (
                <div key={`help-${item.title}`} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#3f4816]/50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#d9fb06]" />
                  </div>
                  <div>
                    <h3 className="text-[#d9fb06] font-semibold mb-1">{item.title}</h3>
                    <p className="text-[#888680] text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance Notice */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#d9fb06] mb-4">Compliance-First Approach</h2>
            <p className="text-[#888680] mb-4">
              We do not use &quot;hacks,&quot; backchannels, or unauthorized access methods. Our work focuses on clear documentation, procedural review, and lawful escalation pathways.
            </p>
            <p className="text-[#888680] text-sm">
              Social Dispute Co. is not a law firm and does not provide legal advice. We do not guarantee reinstatement. Platform decisions are made solely by the platform. If you need legal representation, consult a licensed attorney in your jurisdiction.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="bg-[#d9fb06] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a1c1b] mb-4">
              Ready to Dispute Your {data.name} Suspension?
            </h2>
            <p className="text-[#1a1c1b]/80 mb-8 max-w-2xl mx-auto">
              Submit your information and we'll review your case. Most disputes are prepared within 2-7 days.
            </p>
            <Button 
              onClick={scrollToIntake}
              className="bg-[#1a1c1b] text-[#d9fb06] hover:bg-[#1a1c1b]/90 rounded-full px-8 py-6 font-semibold text-base"
              data-testid="platform-cta-bottom"
            >
              Start Your Dispute Now
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PlatformDisputePage;
